// NOTE: functions_gen2 の node --test が entities/school.js を実行時に読めないので type のみ import する
import type { SubscriptionType } from "./school";

export type LlmUsageLimitValue =
  | { type: "amount"; microUsd: number }
  | { type: "none" } // 上限なし
  | { type: "default" }; // 契約区分の既定値に従う

export type LlmUsageLimitEntry = {
  effectiveFrom: number; // epoch 秒
  limit: LlmUsageLimitValue;
  changedAt: number; // epoch 秒
  changedBy: string; // 登録した運営の uid
  changedByName: string;
  reason: string;
};

export type LlmUsageLimit = {
  effectiveFrom: number; // epoch 秒
  microUsd: number | null; // null は上限なし
};

// 契約区分ごとの上限。学校ごとの値がないときに使う。paid と未設定は上限なしなので持たない。値は functions_gen2 の utils/llmUsageLimit.ts にある
export type LlmUsageLimits = Record<
  "free" | "other",
  LlmUsageLimit[]
>;

// 効いている行（effectiveFrom <= at）のうち最も新しいもの
export function pickEffectiveLlmUsageLimit<T extends { effectiveFrom: number }>(
  rows: T[],
  at: number
): T | null {
  let picked: T | null = null;
  for (const row of rows) {
    if (row.effectiveFrom > at) continue;
    if (picked === null || row.effectiveFrom > picked.effectiveFrom) {
      picked = row;
    }
  }
  return picked;
}

export function resolveLlmUsageLimitMicroUsd(
  input: {
    llmUsageLimits?: LlmUsageLimitEntry[];
    subscriptionType?: SubscriptionType;
    at: number;
  },
  limits: LlmUsageLimits
): number | null {
  const own = pickEffectiveLlmUsageLimit(input.llmUsageLimits ?? [], input.at);
  if (own?.limit.type === "amount") return own.limit.microUsd;
  if (own?.limit.type === "none") return null;
  const { subscriptionType } = input;
  if (subscriptionType !== "free" && subscriptionType !== "other") return null;
  return (
    pickEffectiveLlmUsageLimit(limits[subscriptionType], input.at)
      ?.microUsd ?? null
  );
}

export function isLlmUsageLimitEntry(row: unknown): row is LlmUsageLimitEntry {
  if (typeof row !== "object" || row === null) return false;
  const r = row as Record<string, unknown>;
  if (!Number.isFinite(r.effectiveFrom) || !Number.isFinite(r.changedAt)) {
    return false;
  }
  if (typeof r.changedBy !== "string" || typeof r.changedByName !== "string") {
    return false;
  }
  if (typeof r.reason !== "string" || r.reason.trim() === "") return false;
  const limit = r.limit as { type?: unknown; microUsd?: unknown } | null;
  if (typeof limit !== "object" || limit === null) return false;
  if (limit.type === "none" || limit.type === "default") return true;
  return (
    limit.type === "amount" &&
    Number.isInteger(limit.microUsd) &&
    (limit.microUsd as number) >= 0
  );
}

export type LlmUsageLimitJudgement =
  | { exhausted: false }
  | { exhausted: true; limitMicroUsd: number; usedMicroUsd: number };

export function judgeLlmUsageLimit(input: {
  isStudent: boolean;
  limitMicroUsd: number | null;
  usedMicroUsd: number;
}): LlmUsageLimitJudgement {
  const { isStudent, limitMicroUsd, usedMicroUsd } = input;
  if (!isStudent || limitMicroUsd === null || usedMicroUsd < limitMicroUsd) {
    return { exhausted: false };
  }
  return { exhausted: true, limitMicroUsd, usedMicroUsd };
}

const JST_OFFSET_SECONDS = 9 * 60 * 60;

// NOTE: JST は UTC+9 固定なので 9 時間ずらして UTC の getter で読む（functions_gen2 の llmUsagePeriod.ts と同じ）
export function jstMonthKey(at: number): string {
  const jst = new Date((at + JST_OFFSET_SECONDS) * 1000);
  const month = String(jst.getUTCMonth() + 1).padStart(2, "0");
  return `${String(jst.getUTCFullYear()).padStart(4, "0")}-${month}`;
}

export function nextJstMonthStart(at: number): number {
  const jst = new Date((at + JST_OFFSET_SECONDS) * 1000);
  return (
    Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth() + 1, 1) / 1000 -
    JST_OFFSET_SECONDS
  );
}

export function formatUsd(microUsd: number): string {
  return `$${(microUsd / 1_000_000).toFixed(2)}`;
}

// 1 ドル 150 円で換算した目安
export function formatYenApprox(microUsd: number): string {
  return `約 ${Math.round((microUsd / 1_000_000) * 150)} 円`;
}

export function formatJstDateTime(at: number): string {
  const jst = new Date((at + JST_OFFSET_SECONDS) * 1000);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${jst.getUTCFullYear()}/${two(jst.getUTCMonth() + 1)}/${two(jst.getUTCDate())} ${two(jst.getUTCHours())}:${two(jst.getUTCMinutes())}`;
}

// 下がらないときは null。下がるときは window.confirm に渡す文言
export function llmUsageLimitLoweringConfirmMessage(input: {
  currentMicroUsd: number | null;
  nextMicroUsd: number | null;
  effectiveFrom: number | null; // null は「すぐ」
}): string | null {
  const { currentMicroUsd, nextMicroUsd, effectiveFrom } = input;
  if (nextMicroUsd === null) return null;
  if (currentMicroUsd !== null && nextMicroUsd >= currentMicroUsd) return null;
  const to = formatUsd(nextMicroUsd);
  const head =
    currentMicroUsd === null
      ? `上限を上限なしから ${to} に下げます。`
      : `上限を ${formatUsd(currentMicroUsd)} から ${to} に下げます。`;
  if (effectiveFrom === null) {
    return `${head}\n今月すでに ${to} を超えた生徒は、すぐに使えなくなります。\nよろしいですか？`;
  }
  return `${formatJstDateTime(effectiveFrom)} から、${head}\nその時点でその月の利用が ${to} を超えた生徒は使えなくなります。\nよろしいですか？`;
}

// get_usage_status の答え。availableFrom: 使い切ったときだけ、また使えるようになる時刻（epoch 秒）
// remainingRatio: 上限のある生徒の今月の残り（0〜1）。上限が無い・使い切った・生徒でないときは null
// remainingUntil: remainingRatio が有効な期限（次の月初、epoch 秒）。remainingRatio が null なら null
export type UsageStatus = {
  llmUsage: {
    availableFrom: number | null;
    remainingRatio: number | null;
    remainingUntil: number | null;
  };
  webSearch: { availableFrom: number | null };
};

export function llmUsageRemainingRatio(
  limitMicroUsd: number,
  usedMicroUsd: number
): number {
  if (limitMicroUsd <= 0) return 0;
  return Math.max(0, (limitMicroUsd - usedMicroUsd) / limitMicroUsd);
}
